import React, { RefObject } from "react";
import Video, {
  ConnectOptions,
  LocalAudioTrack,
  LocalDataTrack,
  LocalParticipant,
  LocalTrack,
  LocalVideoTrack,
  RemoteAudioTrack,
  RemoteDataTrack,
  RemoteParticipant,
  RemoteTrack,
  RemoteTrackPublication,
  RemoteVideoTrack,
  Room,
  TwilioError
} from "twilio-video";

import "./VideoComponent.scss";
import { get } from "../../../utils/fetch";
import { Patient } from "../../patient/types";
import { VideoDeviceObject } from "../types";
import { isIE } from "react-device-detect";
import logging from "../../../utils/logging";
import history from "../../../history";

interface Participant {
  participant_uid: string;
  participant_name: string;
}

interface VideoInfo {
  participants: Participant[];
  token: string;
  room: string;
}

interface OwnProps {
  participantId: string;
  patient: Patient | null;
}

interface OwnState {
  videoInfo: VideoInfo | null;
  loading: boolean;
  errorMessage: string | null;
  roomNameErr: boolean; // Track error for room name TextField
  previewTracks: any;
  localMediaAvailable: boolean;
  hasJoinedRoom: boolean;
  activeRoom: Room | null; // Track the current active room
  showRemoteVideo: boolean;
  disableAudio: boolean;
  disableVideo: boolean;
  waitingForParticipant: boolean;
  callEnded: boolean;
  console: string;
  showConsole: boolean;
  videoDeviceIds: VideoDeviceObject[];
  showCameraToggle: boolean;
  currentVideoDevice: string;
}

type Props = OwnProps;

export default class VideoComponent extends React.PureComponent<Props, OwnState> {
  public localMediaRef: RefObject<HTMLDivElement>;
  public remoteMediaRef: RefObject<HTMLDivElement>;
  public changingCamera = false;
  public usingRearCamera = false;
  public isMobile = false;
  public defaultConstraints = { video: true, audio: true };
  constructor(props: Props) {
    super(props);
    this.state = {
      videoInfo: null,
      loading: true,
      errorMessage: null,
      roomNameErr: false, // Track error for room name TextField
      previewTracks: null,
      localMediaAvailable: false,
      hasJoinedRoom: false,
      activeRoom: null, // Track the current active room
      showRemoteVideo: false,
      disableAudio: false,
      disableVideo: false,
      waitingForParticipant: true,
      callEnded: false,
      console: "Console\n",
      showConsole: false,
      videoDeviceIds: [],
      showCameraToggle: false,
      currentVideoDevice: ""
    };
    if (
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Mobi|Opera Mini/i.test(
        navigator.userAgent
      )
    ) {
      this.isMobile = true;
    }

    this.localMediaRef = React.createRef<HTMLDivElement>();
    this.remoteMediaRef = React.createRef<HTMLDivElement>();
  }

  setDebugState = (message: string) => {
    this.setState({ console: this.state.console + "\n" + message });
  };

  fetchVideoInfo = async () => {
    const url = "/api/conferencing/" + this.props.participantId + "/token/";

    const resp = await get<VideoInfo>(url);

    if (resp.error) {
      this.setState({ loading: false, errorMessage: resp.error.message });
    } else if (resp.data) {
      this.setState({ videoInfo: resp.data, loading: false, errorMessage: null });
    } else {
      // TODO Logging
    }
  };

  // This function ensures that the user has granted the browser permission to use audio and video
  // devices. If permission has not been granted, it will cause the browser to ask for permission
  // for audio and video at the same time (as opposed to separate requests).
  ensureMediaPermissions = (constraints: any) => {
    return navigator.mediaDevices
      .enumerateDevices()
      .then(devices => devices.every(device => !(device.deviceId && device.label)))
      .then(shouldAskForMediaPermissions => {
        if (shouldAskForMediaPermissions) {
          navigator.mediaDevices
            .getUserMedia(constraints)
            .then(mediaStream => mediaStream.getTracks().forEach(track => track.stop()));
        }
      });
  };

  handleMediaDeviceConnectErrors = (error: Error) => {
    /* Generic error */
    let errorMessage = "Unable to join video conference due to an unspecified error.";
    /* Handle some specific known circumstances that can cause errors */
    switch (error.name) {
      case "NotAllowedError":
        errorMessage =
          "You must allow this website permission to use your camera and/or microphone before joining the video conference.";
        break;
      case "SecurityError":
        /* Test for HTTPS */
        if (location.protocol === "https:") {
          errorMessage = "Please use HTTPS to securely access this site.";
        } else {
          errorMessage = "A security error is preventing access to your camera and/or microphone.";
        }
        break;
      case "TypeError":
        /* Test for navigator.mediaDevices */
        if (!("mediaDevices" in navigator)) {
          errorMessage =
            "Your device, browser or OS version does not support video conferencing at this time.  Please upgrade to the latest version and try again.";
        }
        break;
    }

    /* eslint-disable-next-line no-console */
    console.log(error);
    logging.error("Could not get user media: " + error);
    this.setDebugState(errorMessage);
    this.setState({ errorMessage: errorMessage });
  };

  async componentDidMount() {
    await this.fetchVideoInfo();
  }

  connectVideo = (token: string | undefined, opts: ConnectOptions) => {
    // Join the Room with the token from the server and the
    // LocalParticipant's Tracks.
    if (!token) {
      /* eslint-disable-next-line no-console */
      console.log("Could not get token for room: " + opts.name);
      const errMsg = `No token for room: ${opts.name}`;
      logging.error(errMsg);
      this.setDebugState(errMsg);
      return;
    }

    //Opts has the label and id of the video
    if (!this.state.currentVideoDevice) {
      const videoLabel = opts.tracks?.find(t => t.kind === "video").label;
      this.setState({ currentVideoDevice: videoLabel });
    }
    Video.connect(token, opts).then(this.roomJoined, (error: TwilioError) => {
      const errorMessage = "Could not connect to Twilio: " + error.message;
      this.setState({ errorMessage: errorMessage });
      logging.error(errorMessage);
    });
  };
  //Just using the facingMode instead of the videoDeviceIds
  getMediaDevices() {
    try {
      navigator.mediaDevices.enumerateDevices().then(devices => {
        devices.forEach(mediaDevice => {
          // If label is empty that means the user has not given us permissions to use camera
          if (mediaDevice.kind === "videoinput") {
            this.setState({
              videoDeviceIds: [
                ...this.state.videoDeviceIds,
                {
                  id: mediaDevice.deviceId,
                  label: mediaDevice.label
                }
              ]
            });
          }
        });
      });
    } catch (error) {
      //TODO: Error handling
    }
  }

  joinRoom = (constraints: any) => {
    try {
      this.ensureMediaPermissions(constraints).then(() =>
        navigator.mediaDevices
          .getUserMedia(constraints)
          .then(stream => {
            if (!this.state.videoInfo?.room.trim()) {
              this.setState({ roomNameErr: true });
              return;
            }

            const connectOptions: ConnectOptions = {
              name: this.state.videoInfo?.room,
              preferredVideoCodecs: ["VP8"],
              tracks: stream.getTracks()
            };
            this.connectVideo(this.state.videoInfo?.token, connectOptions);
          })
          .catch(this.handleMediaDeviceConnectErrors)
      );
    } catch (error) {
      this.handleMediaDeviceConnectErrors(error);
    }
  };

  attachLocalTracks = (
    tracks: (LocalAudioTrack | LocalVideoTrack | LocalDataTrack | null)[],
    container: HTMLDivElement
  ) => {
    //Need to remove the old tracks too
    container.innerHTML = "";
    tracks.forEach(track => {
      if (track && track.kind !== "data") {
        container.appendChild(track.attach());
      }
    });
  };

  attachTracks = (
    tracks: (RemoteAudioTrack | RemoteVideoTrack | RemoteDataTrack | null)[],
    container: HTMLDivElement
  ) => {
    this.setDebugState("Attach Remote Tracks " + tracks.length);
    tracks.forEach(track => {
      if (track && track.kind !== "data") {
        this.setDebugState("Attach Remote Track");
        container.appendChild(track.attach());
      }
    });
  };

  attachLocalParticipantTracks = (participant: LocalParticipant, container: HTMLDivElement) => {
    const tracks = this.getLocalTracks(participant);
    if (tracks) {
      this.attachLocalTracks(tracks, container);
      participant
        .publishTracks(tracks)
        .then(() => this.setDebugState("Published Local Tracks"))
        .catch(r => this.setDebugState("Could not publish local tracks " + r));
    }
    participant.on("trackUnpublished", pub => {
      this.setDebugState("Local Participant Unpbulished Track: " + pub.name);
    });
  };

  getTracks(
    participant: RemoteParticipant
  ): (RemoteAudioTrack | RemoteVideoTrack | RemoteDataTrack | null)[] {
    this.setDebugState("Get Remote Tracks");
    return Array.from(participant.tracks.values())
      .filter(function(publication: RemoteTrackPublication) {
        return publication.track;
      })
      .map(function(publication: RemoteTrackPublication) {
        return publication.track;
      });
  }

  getLocalTracks(participant: LocalParticipant): LocalTrack[] {
    return Array.from(participant.tracks.values())
      .filter(pub => pub.track)
      .map(pub => pub.track);
  }

  detachLocalTracks = (tracks: (LocalAudioTrack | LocalVideoTrack | LocalDataTrack | null)[]) => {
    tracks.forEach(track => {
      if (track && track.kind !== "data") {
        track.stop();
        track.detach().forEach((detachedElement: HTMLElement) => {
          detachedElement.remove();
        });
      }
    });
  };

  detachLocalParticipantTracks = (participant: LocalParticipant) => {
    const tracks = this.getLocalTracks(participant);
    this.detachLocalTracks(tracks);
  };

  attachTrack(track: RemoteTrack | null, container: HTMLDivElement) {
    if (!track) {
      /* eslint-disable-next-line no-console */
      console.log("No track found.");
      this.setDebugState("Attach Remote Track - NO  track found");
      return;
    }

    if (track.kind !== "data") {
      this.setDebugState("Attach Remote Track");
      container.appendChild(track.attach());
    }
  }

  trackPublished(publication: RemoteTrackPublication, container: HTMLDivElement) {
    // if (publication.isSubscribed) {
    //   this.attachTrack(publication.track, container);
    // }
    publication.on("subscribed", (track: RemoteTrack) => {
      this.setState({
        console: this.state.console + "\n" + "Remote track Subscribe " + track.name
      });
      this.attachTrack(track, container);
    });

    publication.on("unsubscribed", (track: RemoteTrack) => {
      this.setState({
        console: this.state.console + "\n" + "Remote track Unsubscribe " + track.name
      });
      this.detachTrack(track);
    });
  }

  participantConnected = (participant: RemoteParticipant, container: HTMLDivElement) => {
    this.setDebugState("Remote Participant Connected");
    participant.tracks.forEach((publication: RemoteTrackPublication) => {
      this.trackPublished(publication, container);
    });
    participant.on("trackPublished", (publication: RemoteTrackPublication) => {
      this.setDebugState("Track Published: " + publication.kind + " " + publication.trackName);
      this.trackPublished(publication, container);
    });

    participant.on("trackUnpublished", (publication: RemoteTrackPublication) => {
      this.setDebugState("Track Unpublished: " + publication.kind + " " + publication.trackName);
    });
    // Check if this is the video track, then show black screen
    participant.on("trackDisabled", (publication: RemoteTrackPublication) => {
      if (publication.track?.kind === "video") {
        participant.videoTracks.forEach(function(videoTrack) {
          if (videoTrack.track && !videoTrack.track.isEnabled) {
            //Detach the track
            videoTrack.track.detach().forEach(function(element: any) {
              element.remove();
            });
          }
        });
      }
      this.setDebugState("trackDisabled: " + publication.kind + " " + publication.trackName);
    });

    //Enable the video track
    participant.on("trackEnabled", (publication: RemoteTrackPublication) => {
      if (publication.track?.kind === "video") {
        participant.audioTracks.forEach(function(audioTrack) {
          if (audioTrack.track) {
            audioTrack.track.detach().forEach(function(element: any) {
              element.remove();
            });
          }
        });
        participant.videoTracks.forEach(function(videoTrack) {
          if (videoTrack.track && videoTrack.track.isEnabled) {
            //Detach audio track and then enable both video and audio. This helps the audio and video desync. Probably is a better way to do this
            container.appendChild(videoTrack.track.attach());
          }
        });
        participant.audioTracks.forEach(function(audioTrack) {
          if (audioTrack.track) {
            container.appendChild(audioTrack.track.attach());
          }
        });
      }
      this.setDebugState("trackEnabled: " + publication.kind + " " + publication.trackName);
    });
  };

  // Detach given track from the DOM.
  detachTrack = (track: any) => {
    track.detach().forEach(function(element: any) {
      element.remove();
    });
  };

  // Detach the Participant's Tracks from the DOM.
  detachParticipantTracks = (participant: any) => {
    const tracks = this.getTracks(participant);
    tracks.forEach(this.detachTrack);
  };

  roomJoined = (room: Room) => {
    this.setState({
      activeRoom: room,
      localMediaAvailable: true,
      hasJoinedRoom: true
    });

    this.setDebugState("Room joined");

    // Attach LocalParticipant's Tracks, if not already attached.
    const previewContainer = this.localMediaRef.current;
    if (previewContainer) {
      this.attachLocalParticipantTracks(room.localParticipant, previewContainer);
    }

    if (room.participants.size > 1) {
      this.setState({ showRemoteVideo: true });
    }

    // Attach the Tracks of the Room's Participants.
    room.participants.forEach((participant: RemoteParticipant) => {
      const previewContainer = this.remoteMediaRef.current;
      this.setDebugState("Calling partipant connected");
      if (previewContainer) {
        this.participantConnected(participant, previewContainer);
      }
    });

    room.localParticipant.on("trackPublished", publication => {
      this.setDebugState("Track Published: " + publication.kind + " " + publication.trackName);
    });

    room.localParticipant.on("trackUnpublished", publication => {
      this.setDebugState("Track Unpublished: " + publication.kind + " " + publication.trackName);
    });

    // When a Participant joins the Room, log the event.
    room.on("participantConnected", (participant: RemoteParticipant) => {
      const previewContainer = this.remoteMediaRef.current;
      if (previewContainer) {
        this.participantConnected(participant, previewContainer);
      }
      if (!this.state.showRemoteVideo) {
        this.setState({ showRemoteVideo: true });
      }
    });

    // When a Participant leaves the Room, detach its Tracks.
    room.on("participantDisconnected", (participant: RemoteParticipant) => {
      this.setDebugState("Participant Disconnected: " + participant.sid);
      this.detachParticipantTracks(participant);
      if (room.participants.size < 1) {
        this.setState({ showRemoteVideo: false });
      }
    });

    // Once the LocalParticipant leaves the room, detach the Tracks
    // of all Participants, including that of the LocalParticipant.
    room.on("disconnected", () => {
      if (!this.changingCamera) {
        if (this.state.previewTracks) {
          this.state.previewTracks.forEach((track: LocalTrack) => {
            if (track?.kind !== "data") {
              track.stop();
            }
          });
        }
        this.detachLocalParticipantTracks(room.localParticipant);
        room.participants.forEach(this.detachParticipantTracks);
        this.setState({
          activeRoom: null,
          hasJoinedRoom: false,
          localMediaAvailable: false,
          callEnded: true
        });
      } else {
        this.changingCamera = false;
      }
    });
  };

  componentDidUpdate = (prevProps: Readonly<Props>, prevState: Readonly<OwnState>) => {
    if (this.state.videoInfo !== null && prevState.videoInfo === null) {
      this.joinRoom(this.defaultConstraints);
      this.getMediaDevices();
    }

    if (this.state.activeRoom?.participants && this.state.activeRoom.participants.size > 0) {
      this.setState({ waitingForParticipant: false });
    }

    if (this.isMobile) {
      this.setState({ showCameraToggle: true });
    }
  };

  toggleAudio = () => {
    const localParticipant = this.state.activeRoom?.localParticipant;

    if (localParticipant) {
      localParticipant.audioTracks.forEach(function(audioTrack) {
        audioTrack.track.isEnabled ? audioTrack.track.disable() : audioTrack.track.enable();
      });
    }

    this.setState({ disableAudio: !this.state.disableAudio });
  };

  toggleVideo = () => {
    const localParticipant = this.state.activeRoom?.localParticipant;

    if (localParticipant) {
      localParticipant.videoTracks.forEach(function(videoTrack) {
        videoTrack.track.isEnabled ? videoTrack.track.disable() : videoTrack.track.enable();
      });
    }

    this.setState({ disableVideo: !this.state.disableVideo });
  };

  stopVideo = () => {
    const localParticipant = this.state.activeRoom?.localParticipant;

    if (localParticipant) {
      localParticipant.videoTracks.forEach(function(videoTrack) {
        videoTrack.track.disable();
      });
    }
  };
  stopAudio = () => {
    const localParticipant = this.state.activeRoom?.localParticipant;

    if (localParticipant) {
      localParticipant.audioTracks.forEach(function(audioTrack) {
        audioTrack.track.disable();
      });
    }
  };

  changeCamera = () => {
    const localParticipant = this.state.activeRoom?.localParticipant;
    if (!localParticipant) {
      return;
    }
    this.changingCamera = true;
    this.stopVideo();
    const videoConstraints = this.usingRearCamera
      ? { facingMode: "user" }
      : { facingMode: "environment" };
    this.usingRearCamera = !this.usingRearCamera;
    const constraints = {
      video: videoConstraints,
      audio: true
    };
    this.joinRoom(constraints);
  };

  leaveRoom = (forceUnmount?: boolean) => {
    this.state.activeRoom?.disconnect();
    this.setState({ hasJoinedRoom: false, localMediaAvailable: false });
    if (history.length > 1 && !forceUnmount) history.goBack();
  };

  componentWillUnmount(): void {
    this.leaveRoom(true);
  }

  render() {
    const { disableAudio, disableVideo, showConsole, showCameraToggle } = this.state;
    if (isIE)
      return (
        <div>
          This feature is not currently supported in Internet Explorer. Please talk to your
          administrator about using a supported browswer such as Google Chrome.
        </div>
      );

    if (!this.state.loading && this.state.errorMessage) {
      return <div id="error">{this.state.errorMessage}</div>;
    }

    if (this.state.callEnded) {
      return (
        <div className="video-component-container">
          <div className="waiting-on">Video call has ended.</div>
        </div>
      );
    }

    if (this.state.videoInfo) {
      return (
        <div className="video-component-container">
          <div className="actions ">
            <button className="fa-btn" onClick={this.toggleAudio}>
              <span className="fa-stack fa-2x">
                <i className="fas fa-circle fa-stack-2x"></i>
                <i className="fas fa-microphone fa-stack-1x fa-inverse"></i>
                {disableAudio && <i className="fal fa-ban fa-stack-2x red"></i>}
              </span>
            </button>
            <button className="fa-btn ml-15 mr-15" onClick={() => this.leaveRoom()}>
              <span className="fa-stack fa-2x">
                <i className="fas fa-circle fa-stack-2x red"></i>
                <i className="fas fa-phone fa-stack-1x fa-inverse phone-down"></i>
              </span>
            </button>
            <button className="fa-btn" onClick={this.toggleVideo}>
              <span className="fa-stack fa-2x">
                <i className="fas fa-circle fa-stack-2x"></i>
                <i className="fas fa-video fa-stack-1x fa-inverse"></i>
                {disableVideo && <i className="fal fa-ban fa-stack-2x red"></i>}
              </span>
            </button>
            {showCameraToggle && (
              <button className="fa-btn" onClick={this.changeCamera}>
                <span className="fa-stack fa-2x">
                  <i className="fas fa-circle fa-stack-2x"></i>
                  <i className="fas fa-sync-alt fa-stack-1x fa-inverse"></i>
                </span>
              </button>
            )}
          </div>

          {this.state.waitingForParticipant && (
            <div className="waiting-on ">
              <div>Waiting on participant to arrive</div>

              <div className="ellipsis-loading"></div>
            </div>
          )}

          <div className="other-user" ref={this.remoteMediaRef} id="remote-media"></div>
          <div className="current-user">
            {this.state.localMediaAvailable && <div className="frame" ref={this.localMediaRef} />}
          </div>
          {showConsole && <div className="debug">{this.state.console}</div>}
        </div>
      );
    }

    return null;
  }
}
